import _ from "lodash";
import { useMemo } from "react";
import { groupsFromPIC } from "../service/resources/data-map/dataMapHelpers";
import {
  useDataMapCollected,
  useDataMapCollectedPIC,
  useDataMapCollectedPICGroups,
  useDataMapDisclosedPIC,
  useDataMapDisclosedPICGroups,
  useDataMapReceivedPIC,
  useDataMapReceivedPICGroups,
} from "../service/resources/data-map/dataMapTransformers";
import { CollectionGroupType } from "../service/server/controller/CollectionGroupsController";
import { CollectionGroupDetailsDto } from "../service/server/dto/CollectionGroupDto";
import {
  CategoryDto,
  CollectionDto,
  DataMapDto,
  DisclosureDto,
  PICGroupWithCategories,
  ReceivedDto,
} from "../service/server/dto/datamap/DataMapDto";
import { DataMapReceivedUpdateOperation } from "../service/server/dto/datamap/DataMapRequestDto";
import {
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
} from "../service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../service/server/Types";

export type DataMapCollectionDetails = {
  collectionDetails: CollectionDto[];
  collectedPic: CategoryDto[];
  collectedPicGroups: PICGroupWithCategories[];
};

const DEFAULT_EXCLUDE_GROUP_TYPE: CollectionGroupType[] = ["EMPLOYMENT"];

export const useAllCollectedPIC = (
  dataMap: DataMapDto,
  excludeCollectionGroupTypes: CollectionGroupType[] = DEFAULT_EXCLUDE_GROUP_TYPE,
  onlyCollectionGroupTypes?: CollectionGroupType[],
) => {
  return useDataMapCollectedPIC(dataMap, {
    exclude: {
      collectionGroupType: excludeCollectionGroupTypes,
    },
    collectionGroupType: onlyCollectionGroupTypes,
  });
};

export const useAllCollectedPICByCollectionGroup = (
  dataMap: DataMapDto,
  group: CollectionGroupDetailsDto,
) => {
  const pics = dataMap.collection
    .filter((col) => col.groupId === group.id)
    .reduce((acc, col) => new Set([...Array.from(acc), col.picId]), new Set<string>());
  return useDataMapCollectedPIC(dataMap).filter((pic) => pics.has(pic.id));
};

export const useAllCollectedPICGroups = (
  dataMap: DataMapDto,
  excludeCollectionGroupTypes: CollectionGroupType[] = DEFAULT_EXCLUDE_GROUP_TYPE,
  onlyCollectionGroupTypes?: CollectionGroupType[],
) => {
  const collectedPic = useAllCollectedPIC(
    dataMap,
    excludeCollectionGroupTypes,
    onlyCollectionGroupTypes,
  );

  return useMemo(() => groupsFromPIC(collectedPic), [collectedPic]);
};

export const usePendingReceivedPICByVendorId = (
  dataMap: DataMapDto,
  collectionGroups?: UUIDString[],
): Record<UUIDString, UUIDString[]> => {
  const allPending: DataMapReceivedUpdateOperation[] = dataMap?._local?.operations?.filter?.(
    (op) => op.target == "RECEIVED" && (!collectionGroups || collectionGroups.includes(op.groupId)),
  ) as DataMapReceivedUpdateOperation[];
  const groupedPending = _.groupBy(allPending, (op) => op.vendorId);
  return _.mapValues(groupedPending, (v) => _.uniq(v.map((op) => op.picId)));
};

export const useCollectionDetails = (
  dataMap: DataMapDto,
  collectionGroup: CollectionGroupDetailsDto,
): DataMapCollectionDetails => {
  const collectionDetails = useDataMapCollected(dataMap, { collectionGroupId: collectionGroup.id });
  const collectedPic = useDataMapCollectedPIC(dataMap, { collectionGroupId: collectionGroup.id });
  const collectedPicGroups = useDataMapCollectedPICGroups(dataMap, {
    collectionGroupId: collectionGroup.id,
  });

  return { collectionDetails, collectedPic, collectedPicGroups };
};

export type DataMapDisclosureDetails = {
  disclosureDetails: DisclosureDto[];
  disclosedPic: CategoryDto[];
  disclosedPicGroups: PICGroupWithCategories[];
};

export const useDisclosureDetails = (
  dataMap: DataMapDto,
  collectionGroup: CollectionGroupDetailsDto,
  dataRecipient: OrganizationDataRecipientDto,
): DataMapDisclosureDetails => {
  const disclosureDetails = useMemo(
    () =>
      dataMap.disclosure.filter(
        (c) => c.groupId === collectionGroup.id && c.vendorId === dataRecipient.vendorId,
      ),
    [dataMap, collectionGroup, dataRecipient],
  );

  const disclosedPicGroups = useDataMapDisclosedPICGroups(dataMap, {
    vendorId: dataRecipient.vendorId,
    collectionGroupId: collectionGroup.id,
  });

  const disclosedPic = useDataMapDisclosedPIC(dataMap, {
    vendorId: dataRecipient.vendorId,
    collectionGroupId: collectionGroup.id,
  });

  return { disclosureDetails, disclosedPic, disclosedPicGroups };
};

export type DataMapSourcedDetails = {
  sourceDetails: ReceivedDto[];
  sourcedPic: CategoryDto[];
  sourcedPicGroups: PICGroupWithCategories[];
};
export const useSourcedDetails = (
  dataMap: DataMapDto,
  collectionGroup: CollectionGroupDetailsDto,
  dataRecipient: OrganizationDataSourceDto,
): DataMapSourcedDetails => {
  const sourceDetails = useMemo(
    () =>
      dataMap.received.filter(
        (c) => c.groupId === collectionGroup.id && c.vendorId === dataRecipient.vendorId,
      ),
    [dataMap, collectionGroup, dataRecipient],
  );

  const sourcedPic = useDataMapReceivedPIC(dataMap, {
    collectionGroupId: collectionGroup.id,
    vendorId: dataRecipient.vendorId,
  });
  const sourcedPicGroups = useDataMapReceivedPICGroups(dataMap, {
    collectionGroupId: collectionGroup.id,
    vendorId: dataRecipient.vendorId,
  });
  return { sourceDetails, sourcedPic, sourcedPicGroups };
};
